import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { openAIService } from '@/services/OpenAIService';

export const WorkshopPractice: React.FC = () => {
  const {
    state,
    dispatch,
    navigateToStep,
    addConversation,
    masterSkill
  } = useWorkshopProgress();

  const [localInput, setLocalInput] = useState(state.userInput || '');

  // Live AI Coaching System
  const getLiveCoaching = async (userText: string) => {
    dispatch({ type: 'SET_LYRA_THINKING', payload: true });

    try {
      const coachingPrompt = `You are Lyra, the AI expert coach for nonprofit professionals. You're conducting a live 5-minute AI quality workshop.

**Your Coaching Style:**
- Socratic method: Ask follow-up questions that guide users to insights
- Real-time analysis: Comment on what they wrote specifically
- Conversational: Warm, encouraging, professional
- Strategic thinking: Push them from surface to deeper impact thinking
- Authentic: Reference Hope Gardens Community Center examples

**Current Workshop Context:** Workshop practice: User analyzing generic AI email output

**User Input:** "${userText}"

**Your Response Should:**
1. Acknowledge what they wrote specifically
2. Ask a strategic follow-up question that deepens their thinking
3. Provide a concrete example or suggestion
4. Keep response under 80 words
5. End with an encouraging next step

**Example Style:**
"I love that you mentioned [specific thing they wrote]! Can you push deeper - what specific change happens when [their goal]? For example, at Hope Gardens, when Maya expanded after-school from 40 to 55 students, the real impact was 15 families could work full-time without childcare worry. What's your version of that transformation?"

Provide live coaching now:`;

      const response = await openAIService.generateText(coachingPrompt, {
        tone: 'conversational',
        maxTokens: 150
      });

      const coachingResponse = response.data?.[0]?.generated_text || response.data?.generated_text ||
        `Great thinking! I can see you're developing strategic insights. Can you be more specific about the impact? What changes when this succeeds?`;

      dispatch({ type: 'SET_LYRA_RESPONSE', payload: coachingResponse });
      addConversation(userText, coachingResponse);

    } catch (error) {
      console.error('Live coaching error:', error);
      dispatch({ type: 'SET_LYRA_RESPONSE', payload:
        `I'm having trouble connecting right now, but I can see you're thinking strategically! Keep developing that insight about impact and transformation.`
      });
    } finally {
      dispatch({ type: 'SET_LYRA_THINKING', payload: false });
    }
  };

  const handleInputChange = (value: string) => {
    setLocalInput(value);
    dispatch({ type: 'SET_USER_INPUT', payload: value });
  };

  const handleContinue = () => {
    dispatch({ type: 'SET_IMPROVEMENT_MADE', payload: true });
    masterSkill(0); // Context Setting
    masterSkill(1); // Impact Specification
    navigateToStep('transformation');
  };

  return (
    <WorkshopStepWrapper
      step="practice"
      title="Hands-On Practice"
      description="Step-by-step improvement with Lyra's guidance"
      showSkillsPreview={true}
      minInputLength={50}
    >
      {/* Live Coaching Interface */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
        <div className="flex items-start space-x-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
            <SupabaseIcon
              iconPath="lyra-avatar.png"
              alt="Lyra"
              size="md"
              className="w-8 h-8"
            />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">Live Coaching with Lyra</h3>
            <p className="text-gray-700">
              {state.lyraResponse || "Let's improve this AI output together! Start by telling me what you think is wrong with this generic email. What would you change first?"}
            </p>
            {state.isLyraThinking && (
              <div className="flex items-center space-x-2 mt-2 text-purple-600">
                <div className="animate-spin w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full"></div>
                <span className="text-sm">Lyra is thinking...</span>
              </div>
            )}
          </div>
        </div>

        {/* Live Chat Interface */}
        <div className="space-y-4">
          <textarea
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            placeholder="Type your thoughts here... Lyra will respond with personalized coaching!"
            value={localInput}
            onChange={(e) => handleInputChange(e.target.value)}
            rows={3}
          />
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {localInput.length} characters • Lyra responds to 20+ characters
            </div>
            <Button
              onClick={() => {
                if (localInput.trim().length > 20) {
                  getLiveCoaching(localInput);
                  setLocalInput('');
                  handleInputChange('');
                }
              }}
              disabled={localInput.trim().length < 20 || state.isLyraThinking}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Get Live Coaching
            </Button>
          </div>
        </div>

        {/* Conversation History */}
        {state.conversationHistory.length > 0 && (
          <div className="mt-6 space-y-3 max-h-64 overflow-y-auto">
            <h4 className="font-semibold text-gray-900">Coaching Conversation</h4>
            {state.conversationHistory.map((exchange, index) => (
              <div key={index} className="space-y-2">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-600">You:</div>
                  <div className="text-gray-800">{exchange.user}</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="text-sm font-medium text-purple-600">Lyra:</div>
                  <div className="text-gray-800">{exchange.lyra}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interactive Practice Section */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Left: Guided Practice */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lyra's Step-by-Step Coaching</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-semibold text-purple-700">1</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Add Specific Context</h4>
                  <p className="text-sm text-gray-600">Replace "Supporter" with "Sarah" - make it personal</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-semibold text-purple-700">2</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Include Real Impact</h4>
                  <p className="text-sm text-gray-600">Show exactly how the $75 created change for Marcus</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-semibold text-purple-700">3</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Add Authentic Voice</h4>
                  <p className="text-sm text-gray-600">Replace "The Team" with "Maya Rodriguez, Program Coordinator"</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-semibold text-purple-700">4</div>
                <div>
                  <h4 className="font-semibold text-gray-800">Create Engagement</h4>
                  <p className="text-sm text-gray-600">Add invitation to visit and see impact firsthand</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath="lyra-avatar.png"
                  alt="Lyra"
                  size="md"
                  className="w-8 h-8"
                />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 mb-2">Lyra's Live Guidance</h4>
                <p className="text-gray-700 text-sm">
                  "Try rewriting the email using these improvements. Focus on making it feel like it comes from
                  a real person (Maya) talking about a real child (Marcus) whose life changed because of this
                  specific donor (Sarah). Make it authentic and engaging!"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Practice Area */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Practice Area</CardTitle>
              <CardDescription>Rewrite the email using Lyra's guidance</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Dear Sarah,

Your $75 monthly gift..."
                value={state.userInput}
                onChange={(e) => handleInputChange(e.target.value)}
                className="min-h-48 text-sm"
              />
              <div className="mt-4 text-xs text-gray-500">
                Tip: Include specific names, dollar amounts, concrete outcomes, and a personal signature
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleContinue}
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={state.userInput.length < 50}
          >
            {state.userInput.length < 50 ? 'Keep writing...' : 'Show Me the Professional Version →'}
          </Button>
        </div>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopPractice;